import ServiceButton from '../ui/ServiceButton'

interface Step {
  id: string
  title: string
  description: string
  bgColor: 'light' | 'orange' | 'dark'
}

const steps: Step[] = [
  {
    id: "01",
    title: "Talk to Our Team",
    description: "A dedicated account manager listens to your delivery goals, geography, and business model.",
    bgColor: 'light'
  },
  {
    id: "02", 
    title: "We Assess Scale & Volume",
    description: "We evaluate your item types, shipping frequency, peak hours, and customer spread.",
    bgColor: 'orange'
  },
  {
    id: "03",
    title: "Get Custom Pricing", 
    description: "No generic pricing. You get tailored delivery rates by zone and volume commitment.",
    bgColor: 'light'
  },
  {
    id: "04",
    title: "Orders Start Flowing",
    description: "Once you're onboarded, our orange-branded fleet kicks into motion. Fast pickups, faster drop-offs.",
    bgColor: 'dark'
  },
  {
    id: "05",
    title: "Track, Verify & Optimize",
    description: "Monitor each delivery. Contact delivery personnel. Analyze reports. Adjust operations anytime.",
    bgColor: 'light'
  }
]

function HowItWorks() {
  const getStepClasses = (bgColor: string) => {
    switch (bgColor) {
      case 'light':
        return 'bg-gray-100 text-gray-800'
      case 'orange':
        return 'bg-[#F55109] text-white'
      case 'dark':
        return 'bg-[#2C2C2C] text-white'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Handler for "Let's Talk Logistics" button
  const handleLetsTalkLogisticsClick = () => {
    window.open(
      'https://outlook.office365.com/owa/calendar/LetsTalk@Speedyssey.onmicrosoft.com/bookings/',
      '_blank',
      'noopener,noreferrer'
    )
  }

  return (
    <section className="bg-white py-20 px-4">
      <div className="container sm:px-10 px-3  mx-auto">
        {/* 3x2 Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Header Card - Top Left */}
          <div className="bg-white p-8 h-96 flex flex-col justify-between">
            <div>
              <p className="text-[#F55109] text-base font-semibold tracking-wider mb-4 uppercase">
                How It Works
              </p>
              
              <h2 className="text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-6 leading-tight" style={{ fontFamily: 'Antonio, sans-serif' }}>
                <span className="text-[#F55109]">OPTIMIZED</span> FOR<br />
                YOUR OPERATIONS
              </h2>
              
              <p className="text-gray-600 text-base mb-8 leading-relaxed">
                From discovery to delivery, our model adapts to your unique logistics flow.
              </p>
            </div>

            <div className="flex">
              <ServiceButton
                title="Let's Talk Logistics"
                className="w-auto"
                onClick={handleLetsTalkLogisticsClick}
              />
            </div>
          </div>

          {/* Steps container */}
          <div className="flex overflow-x-auto md:contents snap-x snap-mandatory">
            {steps.map((step) => (
              <div 
                key={step.id}
                className={`p-8 relative overflow-hidden h-96 flex flex-col justify-between ${getStepClasses(step.bgColor)} w-80 flex-shrink-0 md:w-auto md:flex-shrink snap-start`}
              >
                {/* Large Number Background */}
                <div className="absolute top-4 right-4 opacity-20">
                  <span 
                    className="text-8xl font-bold"
                    style={{ fontFamily: 'Antonio, sans-serif' }}
                  >
                    {step.id}
                  </span>
                </div>
                
                {/* Content */}
                <div className="relative z-10 flex flex-col h-full justify-end">
                  <h3 
                    className="text-2xl font-bold mb-6"
                    style={{ fontFamily: 'Antonio, sans-serif' }}
                  >
                    {step.title}
                  </h3>
                  <p className={`text-base leading-relaxed ${
                    step.bgColor === 'orange' || step.bgColor === 'dark' 
                      ? 'text-white/90' 
                      : 'text-gray-600'
                  }`}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
